import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Toaster, toast } from 'sonner';
import { Star, ArrowLeft, CircleNotch, Target, CaretUp, CaretDown, Flame } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { ExportButton } from '../components/ExportCSV';
import WindowNarrative from '../components/WindowNarrative';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BALL_KEYS = ['ball_1', 'ball_2', 'ball_3', 'ball_4', 'ball_5'];

const OverdueBadge = ({ ratio }) => {
  if (ratio >= 2) return <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-red-100 text-red-800" data-testid="overdue-critical">CRITICAL</span>;
  if (ratio >= 1.5) return <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-orange-100 text-orange-800" data-testid="overdue-high">HIGH</span>;
  if (ratio >= 1) return <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-100 text-amber-800" data-testid="overdue-due">DUE</span>;
  return <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-green-100 text-green-800" data-testid="overdue-ok">OK</span>;
};

/* ================================================================
 * W13 Top 10 Most Overdue — per ball, + cross-ball aggregation
 * ================================================================ */
function W13TopOverduePanel({ data }) {
  const [last88, setLast88] = useState(null);

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/draws`, { params: { page: 1, limit: 88, sort_by: 'draw_number', sort_order: 'desc' } })
      .then((res) => { if (alive) setLast88(res.data.draws || []); })
      .catch(() => { if (alive) setLast88([]); });
    return () => { alive = false; };
  }, []);

  const balls = data?.balls || {};
  const perBall = BALL_KEYS.map((bk) => {
    const nums = balls[bk] || [];
    const top10 = [...nums]
      .filter((n) => (n.overdue_ratio || 0) > 0)
      .sort((a, z) => (z.overdue_ratio || 0) - (a.overdue_ratio || 0))
      .slice(0, 10);
    return { bk, pos: bk.replace('ball_', ''), top10 };
  });

  // Cross-ball aggregation: numbers that show up in the top-10-overdue list of 2+ balls
  const crossMap = {};
  perBall.forEach(({ top10 }) => {
    top10.forEach((n) => {
      if (!crossMap[n.number]) crossMap[n.number] = { number: n.number, count: 0, sum: 0 };
      crossMap[n.number].count += 1;
      crossMap[n.number].sum += n.overdue_ratio || 0;
    });
  });
  const crossList = Object.values(crossMap).filter((c) => c.count >= 2).sort((a, b) => a.number - b.number);
  const crossNumbers = new Set(crossList.map((c) => c.number));

  // Tier breakdown: "2X" = exactly overdue in 2 balls, "3X" = exactly 3,
  // "4X" = 4 or more (also absorbs the rare 5-ball case). Mutually
  // exclusive so the 3 boxes partition the full "2+" set with no overlap.
  const tiers = [
    { label: '4X', numbers: crossList.filter((c) => c.count >= 4).map((c) => c.number) },
    { label: '3X', numbers: crossList.filter((c) => c.count === 3).map((c) => c.number) },
    { label: '2X', numbers: crossList.filter((c) => c.count === 2).map((c) => c.number) },
  ];

  // Last-88-draws pooled hit check, computed separately PER TIER: for each
  // tier's numbers, did ANY of them land ANYWHERE among the 5 actual balls
  // (any position) on each of the last 88 actual draws?
  const tierStats = tiers.map((t) => {
    const numSet = new Set(t.numbers);
    if (!last88) return { ...t, pct: null, hits: null, total: null, currentGap: null, pctOfAll: Math.round((t.numbers.length / 50) * 1000) / 10 };
    if (t.numbers.length === 0) return { ...t, pct: null, hits: 0, total: last88.length, currentGap: null, pctOfAll: 0 };
    const total = last88.length;
    const isHit = (d) => BALL_KEYS.some((bk) => numSet.has(d[bk]));
    const hits = last88.filter(isHit).length;
    const firstHitIdx = last88.findIndex(isHit);
    const currentGap = firstHitIdx === -1 ? null : firstHitIdx;
    const pctOfAll = Math.round((t.numbers.length / 50) * 1000) / 10;
    return { ...t, pct: Math.round((hits / total) * 1000) / 10, hits, total, currentGap, pctOfAll };
  });

  const tierColor = (ratio) => (ratio >= 2 ? 'text-red-600' : ratio >= 1.5 ? 'text-red-500' : 'text-orange-500');

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6" data-testid="w13-top-overdue-panel">
      <h3 className="font-bold text-lg text-gray-900 mb-3 flex items-center gap-2">
        <Flame size={20} weight="bold" className="text-red-500" /> Top 10 Most Overdue — per Ball
      </h3>
      <div className="grid grid-cols-1 xl:grid-cols-[1fr_auto] gap-6">
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
          {perBall.map(({ pos, top10 }) => (
            <div key={pos} data-testid={`top-overdue-ball_${pos}`}>
              <div className="text-xs font-bold uppercase text-gray-500 mb-1 text-center">Ball {pos}</div>
              <table className="w-full text-xs border border-gray-100 rounded overflow-hidden">
                <tbody>
                  {top10.map((n) => (
                    <tr
                      key={n.number}
                      className={`border-b border-gray-100 ${crossNumbers.has(n.number) ? 'bg-rose-100' : ''}`}
                      data-testid={`top-overdue-ball_${pos}-${n.number}`}
                    >
                      <td className="px-1.5 py-1 font-mono font-bold text-center">{n.number}</td>
                      <td className={`px-1.5 py-1 font-mono font-bold text-right ${tierColor(n.overdue_ratio)}`}>{n.overdue_ratio}x</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
        <div data-testid="w13-top-overdue-crossball">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-2" data-testid="w13-cross-hit-rate-tiers">
            {tierStats.map((t) => (
              <div key={t.label} className="rounded-md bg-indigo-50 border border-indigo-200 px-2 py-1.5" data-testid={`w13-cross-hit-rate-${t.label.toLowerCase()}`}>
                <div className="text-[10px] font-black text-indigo-800">
                  {t.label} <span className="font-normal text-indigo-500">({t.numbers.length} number{t.numbers.length === 1 ? '' : 's'} · {t.pctOfAll}% of 1-50)</span>
                </div>
                <div className="text-[9px] text-indigo-600 mb-0.5">Last 88 draws — any hit, any position?</div>
                <div className="text-lg font-black font-mono text-indigo-700">
                  {t.pct != null ? `${t.pct}%` : t.total === null ? '…' : 'N/A'}
                  {t.pct != null && (
                    <span className="text-[10px] font-normal text-indigo-500 ml-1">({t.hits}/{t.total})</span>
                  )}
                </div>
                {t.pct != null && (
                  <div className="text-[10px] font-bold text-indigo-600 mt-0.5" data-testid={`w13-cross-hit-gap-${t.label.toLowerCase()}`}>
                    {t.currentGap === 0
                      ? 'Last hit: this draw (gap 0)'
                      : t.currentGap != null
                        ? `Last hit: ${t.currentGap} draw${t.currentGap !== 1 ? 's' : ''} ago (gap: ${t.currentGap})`
                        : `No hit in last ${t.total}+ draws`}
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="text-xs font-bold uppercase text-gray-500 mb-1">Overdue in 2+ Balls</div>
          <table className="text-xs border border-gray-200 rounded overflow-hidden">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-2 py-1.5 uppercase text-[10px] text-gray-700">Number</th>
                <th className="px-2 py-1.5 uppercase text-[10px] text-gray-700">Balls</th>
                <th className="px-2 py-1.5 uppercase text-[10px] text-gray-700">Sum of X</th>
              </tr>
            </thead>
            <tbody>
              {crossList.map((c) => (
                <tr key={c.number} className="border-b border-gray-100 bg-rose-50" data-testid={`w13-top-overdue-cross-${c.number}`}>
                  <td className="px-2 py-1 text-center font-mono font-bold">{c.number}</td>
                  <td className="px-2 py-1 text-center font-mono">{c.count}</td>
                  <td className="px-2 py-1 text-center font-mono">{c.sum.toFixed(2)}</td>
                </tr>
              ))}
              {crossList.length === 0 && (
                <tr><td colSpan={3} className="px-2 py-2 text-center text-gray-400">No overlap right now</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      <p className="text-[11px] text-gray-500 mt-3 italic">Rows highlighted in rose also appear in the &quot;Overdue in 2+ Balls&quot; table — same number is overdue at multiple ball positions right now.</p>
    </div>
  );
}

/* ================================================================
 * W13 Bucket Accuracy — T5 / T10 / T15 / T20 hit rate per ball
 * (pulled from the shared W50 walk-forward backtest, filtered to W13)
 * ================================================================ */
const BUCKET_DEFS = [['T5', 10, 0, 5], ['T10', 20, 5, 10], ['T15', 30, 10, 15], ['T20', 40, 15, 20]];

function W13BucketAccuracyPanel() {
  const [loading, setLoading] = useState(true);
  const [bucket, setBucket] = useState(null);
  const [meta, setMeta] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/analysis/window-accuracy`);
        const w13 = (res.data.ball_accuracy || []).find((w) => w.window_id === 'W13');
        if (w13) {
          setBucket(w13.bucket_diagnostic);
          setMeta({ testCount: res.data.test_draws_count, rank: w13.rank, top5: w13.top5_hit_rate, top10: w13.top10_hit_rate });
        }
      } catch { /* non-critical panel — fail silently */ }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6 flex justify-center" data-testid="w13-bucket-accuracy-loading">
      <CircleNotch size={20} className="animate-spin text-[#002FA7]" />
    </div>
  );
  if (!bucket) return null;

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6" data-testid="w13-bucket-accuracy-panel">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <h3 className="font-bold text-lg text-gray-900">Bucket Accuracy — T5 / T10 / T15 / T20 per Ball</h3>
        {meta && <span className="text-xs text-gray-500">Walk-forward over last {meta.testCount} draws · overall rank #{meta.rank} of 21 windows</span>}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-5 gap-3">
        {BALL_KEYS.map((bk) => {
          const diag = bucket[bk];
          const pos = bk.replace('ball_', '');
          if (!diag) return null;
          const top20 = diag.current_top20 || [];
          return (
            <div key={bk} className="border border-gray-100 rounded-lg p-3" data-testid={`w13-bucket-${pos}`}>
              <div className="text-xs font-bold uppercase text-gray-500 mb-2 text-center">Ball {pos}</div>
              <div className="space-y-2">
                {BUCKET_DEFS.map(([label, baseline, start, end]) => {
                  const b = diag.buckets?.[label];
                  const pct = b?.pct ?? 0;
                  const streak = diag.bucket_miss_streaks?.[label] ?? 0;
                  const color = pct >= baseline ? 'text-emerald-600' : 'text-red-500';
                  const nums = top20.slice(start, end);
                  return (
                    <div key={label} className="border-b border-gray-100 pb-1.5 last:border-0 last:pb-0" data-testid={`w13-bucket-${pos}-${label}`}>
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] uppercase text-gray-400 font-bold">{label}</span>
                        <span className={`text-sm font-bold font-mono ${color}`}>{pct}%</span>
                      </div>
                      <div className="flex flex-wrap gap-1 mt-1" data-testid={`w13-bucket-${pos}-${label}-numbers`}>
                        {nums.map((n) => (
                          <span key={n} className="inline-flex w-5 h-5 rounded-full bg-gray-100 text-gray-800 items-center justify-center font-mono text-[10px] font-bold">{n}</span>
                        ))}
                      </div>
                      <div className="text-[9px] text-gray-400 mt-1">streak {streak}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
      <p className="text-[11px] text-gray-500 mt-3 italic">Green = above random baseline (T5&gt;10%, T10&gt;20%, T15&gt;30%, T20&gt;40%). Numbers shown are the current-ranking picks that fall in each bucket. Streak = consecutive recent draws that missed this bucket.</p>
    </div>
  );
}



/* ================================================================
 * W13 Introduction — What this window analyses
 * ================================================================ */
function buildW13Intro(data) {
  const total = data?.total_draws;
  const latest = data?.latest_draw;
  const latestDate = data?.latest_date;
  return {
    testid: 'w13-introduction',
    body: (
      <>
        Window 13 is the <strong>Gap / Overdue Analysis</strong> engine. For each of the five main-ball positions and every candidate number 1–50, it measures the <strong>gap</strong> — how many draws have passed since that number last appeared at this position — and compares it against that number&apos;s own historical average gap.
      </>
    ),
    bullets: [
      <>
        <strong>Overdue ratio</strong> = Current Gap ÷ Average Gap. A ratio of <strong>1.0x</strong> means the number is exactly at its usual rhythm; <strong>1.5x+</strong> is flagged <span className="font-bold text-orange-700">HIGH</span>, <strong>2x+</strong> is flagged <span className="font-bold text-red-700">CRITICAL</span> (the number has gone twice as long without appearing as it normally does).
      </>,
      <>
        <strong>Data window</strong>: computed across all {total ? total.toLocaleString() : '—'} historical draws{latest ? <>, latest being <strong>#{latest}</strong>{latestDate ? ` on ${latestDate}` : ''}</> : ''}. Recalculates automatically whenever a new draw is entered.
      </>,
      <>
        <strong>Prediction score (walk-forward backtested)</strong>: <code className="bg-white/60 px-1 rounded">Score = 90% Frequency + 10% Overdue ratio</code> (both normalized 0–10). This replaces the earlier gap-heavy formula — a backtest across the last 150 draws showed raw &quot;overdue&quot; signals alone barely beat random chance (Top-5 ≈ 11.7%, Top-10 ≈ 24.1%), while a frequency-led blend more than doubled accuracy (Top-5 ≈ 27.5%, Top-10 ≈ 52–53%). Overdue is kept as a 10% tiebreaker rather than dropped entirely.
      </>,
      <>
        <strong>Columns</strong>: Times (historical appearances) · Gap Now / Avg Gap / Std / Min / Max (gap statistics) · Overdue (current ÷ average) · Std OD (overdue expressed in standard deviations) · Score (the blended prediction score above).
      </>,
    ],
  };
}

/* ================================================================
 * W13 Observations — Key findings (auto-computed across the 5 balls)
 * ================================================================ */
function buildW13Observations(data) {
  const balls = data?.balls || {};
  const perBall = BALL_KEYS.map((bk) => {
    const nums = balls[bk] || [];
    if (!nums.length) return null;
    const critical = nums.filter((n) => n.overdue_ratio >= 2);
    const high = nums.filter((n) => n.overdue_ratio >= 1.5 && n.overdue_ratio < 2);
    const topOverdue = [...nums].sort((a, z) => (z.overdue_ratio || 0) - (a.overdue_ratio || 0))[0];
    const topFreq = [...nums].sort((a, z) => (z.times || 0) - (a.times || 0))[0];
    return { pos: bk.replace('ball_', ''), critical, high, topOverdue, topFreq };
  }).filter(Boolean);

  const totalCritical = perBall.reduce((s, p) => s + p.critical.length, 0);
  const totalHigh = perBall.reduce((s, p) => s + p.high.length, 0);
  const mostOverdueOverall = [...perBall].sort((a, z) => (z.topOverdue?.overdue_ratio || 0) - (a.topOverdue?.overdue_ratio || 0))[0];

  return {
    testid: 'w13-observations',
    body: 'Auto-generated summary of the current overdue landscape across the 5 ball positions.',
    bullets: [
      totalCritical > 0 && {
        content: <><strong>{totalCritical} CRITICAL number{totalCritical > 1 ? 's' : ''}</strong> (2x+ overdue) and <strong>{totalHigh} HIGH</strong> (1.5x+) detected across all 5 balls right now.</>,
      },
      mostOverdueOverall?.topOverdue && {
        content: <><strong>Most overdue overall</strong>: Ball {mostOverdueOverall.pos} — number <strong>{mostOverdueOverall.topOverdue.number}</strong> is <strong>{mostOverdueOverall.topOverdue.overdue_ratio}x</strong> its average gap (last seen {mostOverdueOverall.topOverdue.current_gap} draws ago, average is every {mostOverdueOverall.topOverdue.avg_gap} draws).</>,
      },
      ...perBall.map((p) => ({
        content: <><strong>Ball {p.pos}</strong>: {p.critical.length} critical, {p.high.length} high · most frequent historically = <strong>#{p.topFreq?.number}</strong> ({p.topFreq?.times}x) · most overdue = <strong>#{p.topOverdue?.number}</strong> ({p.topOverdue?.overdue_ratio}x).</>,
      })),
    ].filter(Boolean),
    footer: 'A high overdue ratio alone is a weak predictor (see Introduction) — frequency is weighted far more heavily in the Score column below.',
  };
}

/* ================================================================
 * W13 Key Findings — applicable to next draw
 * ================================================================ */
function buildW13KeyFindings(data) {
  const balls = data?.balls || {};
  const picks = BALL_KEYS.map((bk) => {
    const nums = balls[bk] || [];
    if (!nums.length) return null;
    const top = [...nums].sort((a, z) => (z.pred_score || 0) - (a.pred_score || 0))[0];
    if (!top) return null;
    return { pos: bk.replace('ball_', ''), top };
  }).filter(Boolean);

  const ticket = picks.map((p) => p.top.number).sort((a, b) => a - b);

  return {
    testid: 'w13-key-findings',
    title: 'Key Findings — applicable to next draw',
    body: 'Top pick per ball under the new 90% Frequency + 10% Overdue score, followed by the composed ticket.',
    badge: ticket.length === 5 ? { color: 'from-emerald-600 to-teal-700', icon: '🎯', label: 'W13 composed ticket', detail: ticket.join(' · ') } : null,
    bullets: picks.map((p) => ({
      content: <>
        <strong>Ball {p.pos}</strong> → top pick <span className="inline-flex w-7 h-7 rounded-full bg-emerald-600 text-white items-center justify-center font-mono font-black text-xs mx-1">{p.top.number}</span>
        <span className="text-xs text-gray-600 font-mono">
          score {p.top.pred_score} · seen {p.top.times}x historically · gap {p.top.current_gap} (avg {p.top.avg_gap}) · {p.top.overdue_ratio}x overdue
        </span>
      </>,
    })),
    footer: 'These are model-suggested candidates only; consult W53 (Ball Master) or W62 (Consolidated Verdict) for the cross-window consensus.',
  };
}

export default function Window13GapAnalysis() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'pred_score', dir: 'desc' });

  useEffect(() => {
    (async () => {
      try { const res = await axios.get(`${API}/analysis/gap-analysis`); setData(res.data); }
      catch { toast.error('Failed to fetch gap analysis'); }
      finally { setLoading(false); }
    })();
  }, []);

  const handleSort = (key) => setSortConfig(prev => ({ key, dir: prev.key === key && prev.dir === 'desc' ? 'asc' : 'desc' }));
  const SortIcon = ({ k }) => sortConfig.key !== k ? null : sortConfig.dir === 'desc' ? <CaretDown size={10} /> : <CaretUp size={10} />;

  const ballLabels = { ball_1: 'Ball 1', ball_2: 'Ball 2', ball_3: 'Ball 3', ball_4: 'Ball 4', ball_5: 'Ball 5' };

  return (
    <div className="min-h-screen bg-gray-50">
      <Toaster position="top-right" richColors />
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" onClick={() => navigate('/')} data-testid="back-btn"><ArrowLeft size={18} /></Button>
              <div className="w-10 h-10 bg-[#002FA7] rounded flex items-center justify-center"><Star size={24} weight="fill" className="text-amber-400" /></div>
              <div><h1 className="text-2xl font-bold text-gray-900">Window 13</h1><p className="text-xs text-gray-500">Gap Analysis</p></div>
            </div>
            <ExportButton data={data} filename="w13_gap_analysis" />
          </div>
        </div>
      </header>
      <main className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {loading ? <div className="flex justify-center py-20"><CircleNotch size={32} className="animate-spin text-[#002FA7]" /></div> : data ? (
          <>
            <div className="mb-6">
              <WindowNarrative windowId="W13" intro={buildW13Intro(data)} observations={buildW13Observations(data)} keyFindings={buildW13KeyFindings(data)} />
            </div>
            <W13TopOverduePanel data={data} />
            <W13BucketAccuracyPanel />
            <Tabs defaultValue="ball_1" className="space-y-4">
            <TabsList className="bg-white border border-gray-200 p-1 flex-wrap">
              {Object.entries(ballLabels).map(([k, l]) => (
                <TabsTrigger key={k} value={k} className="data-[state=active]:bg-[#002FA7] data-[state=active]:text-white font-bold" data-testid={`tab-${k}`}>{l}</TabsTrigger>
              ))}
            </TabsList>
            {Object.entries(ballLabels).map(([key, label]) => {
              const nums = data.balls?.[key] || [];
              const overdueCritical = nums.filter(n => n.overdue_ratio >= 2).length;
              const overdueHigh = nums.filter(n => n.overdue_ratio >= 1.5 && n.overdue_ratio < 2).length;
              const sorted = [...nums].sort((a, b) => {
                let aV = a[sortConfig.key] || 0, bV = b[sortConfig.key] || 0;
                return sortConfig.dir === 'desc' ? bV - aV : aV - bV;
              });
              const topPredicted = new Set([...nums].sort((a, b) => (b.pred_score || 0) - (a.pred_score || 0)).slice(0, 5).map(n => n.number));
              return (
                <TabsContent key={key} value={key}>
                  <div className="flex gap-3 mb-4 text-xs font-bold">
                    <span className="px-2 py-1 bg-red-100 text-red-800 rounded">CRITICAL (2x+): {overdueCritical}</span>
                    <span className="px-2 py-1 bg-orange-100 text-orange-800 rounded">HIGH (1.5x+): {overdueHigh}</span>
                    <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded">Total Numbers: {nums.length}</span>
                  </div>
                  <div className="bg-white border border-gray-200 rounded overflow-hidden">
                    <div className="overflow-x-auto" data-testid="gap-table">
                      <table className="w-full text-xs">
                        <thead className="bg-gray-100">
                          <tr>
                            {[['number','#'],['times','Times'],['current_gap','Gap Now'],['avg_gap','Avg Gap'],['std_gap','Std'],['min_gap','Min'],['max_gap','Max'],['overdue_ratio','Overdue'],['overdue_std','Std OD'],['pred_score','Score']].map(([k,l]) => (
                              <th key={k} className="px-2 py-2 text-[10px] uppercase text-gray-700 cursor-pointer hover:bg-gray-200 text-center" onClick={() => handleSort(k)}>{l} <SortIcon k={k} /></th>
                            ))}
                            <th className="px-2 py-2 text-[10px] uppercase text-gray-700 text-center">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {sorted.map((n, idx) => (
                            <tr key={n.number} className={topPredicted.has(n.number) ? 'bg-blue-100' : idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} style={topPredicted.has(n.number) ? {borderLeft: '4px solid #2563eb', backgroundColor: 'rgba(37, 99, 235, 0.18)'} : {}} data-testid={`gap-row-${n.number}`}>
                              <td className="px-2 py-1 border-b font-mono font-bold text-center">{n.number}</td>
                              <td className="px-2 py-1 border-b font-mono text-center font-bold">{n.times}</td>
                              <td className={`px-2 py-1 border-b font-mono text-center font-bold ${n.current_gap > n.avg_gap ? 'text-red-600' : 'text-green-700'}`}>{n.current_gap}</td>
                              <td className="px-2 py-1 border-b font-mono text-center">{n.avg_gap}</td>
                              <td className="px-2 py-1 border-b font-mono text-center text-[10px]">{n.std_gap}</td>
                              <td className="px-2 py-1 border-b font-mono text-center text-[10px]">{n.min_gap}</td>
                              <td className="px-2 py-1 border-b font-mono text-center text-[10px]">{n.max_gap}</td>
                              <td className={`px-2 py-1 border-b font-mono text-center font-bold ${n.overdue_ratio > 1.5 ? 'text-red-600' : n.overdue_ratio > 1 ? 'text-orange-600' : 'text-gray-500'}`}>{n.overdue_ratio}x</td>
                              <td className={`px-2 py-1 border-b font-mono text-center ${n.overdue_std > 1 ? 'text-red-600 font-bold' : 'text-gray-400'}`}>{n.overdue_std}</td>
                              <td className="px-2 py-1 border-b font-mono text-center font-bold text-sm">{n.pred_score}</td>
                              <td className="px-2 py-1 border-b text-center"><OverdueBadge ratio={n.overdue_ratio} /></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Prediction */}
                  <div className="bg-gradient-to-r from-indigo-700 to-blue-800 rounded-lg p-5 mt-4 text-white">
                    <div className="flex items-center gap-2 mb-2"><Target size={24} weight="bold" /><h3 className="font-bold text-xl">Predicted Next {label} (Overdue & High Frequency)</h3></div>
                    <p className="text-xs text-white/70 mb-4">Score = <strong>90% Frequency</strong> + 10% Overdue ratio (both normalized 0–10) — backtested for best accuracy</p>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                      {[...nums].sort((a, b) => b.pred_score - a.pred_score).slice(0, 10).map((n, idx) => (
                        <div key={n.number} className={`p-3 rounded-lg ${idx === 0 ? 'bg-sky-100 text-sky-900' : 'bg-white/10'}`} data-testid={`pred-${n.number}`}>
                          <div className="text-2xl font-mono font-bold text-center">{n.number}</div>
                          <div className="text-xs text-center mt-1 opacity-80">Gap: {n.current_gap} | {n.overdue_ratio}x overdue</div>
                          <div className="text-center font-bold mt-1">{n.pred_score}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </TabsContent>
              );
            })}
            </Tabs>
          </>
        ) : null}
      </main>
    </div>
  );
}

export function Window13Content() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'pred_score', dir: 'desc' });

  useEffect(() => {
    (async () => {
      try { const res = await axios.get(`${API}/analysis/gap-analysis`); setData(res.data); }
      catch { toast.error('Failed to fetch gap analysis'); }
      finally { setLoading(false); }
    })();
  }, []);

  const handleSort = (key) => setSortConfig(prev => ({ key, dir: prev.key === key && prev.dir === 'desc' ? 'asc' : 'desc' }));
  const SortIcon = ({ k }) => sortConfig.key !== k ? null : sortConfig.dir === 'desc' ? <CaretDown size={10} /> : <CaretUp size={10} />;
  const ballLabels = { ball_1: 'Ball 1', ball_2: 'Ball 2', ball_3: 'Ball 3', ball_4: 'Ball 4', ball_5: 'Ball 5' };

  if (loading) return <div className="flex justify-center py-20"><CircleNotch size={32} className="animate-spin text-[#002FA7]" /></div>;
  if (!data) return null;

  return (
    <Tabs defaultValue="ball_1" className="space-y-4">
      <TabsList className="bg-white border border-gray-200 p-1 flex-wrap">
        {Object.entries(ballLabels).map(([k, l]) => (
          <TabsTrigger key={k} value={k} className="data-[state=active]:bg-[#002FA7] data-[state=active]:text-white font-bold">{l}</TabsTrigger>
        ))}
      </TabsList>
      {Object.entries(ballLabels).map(([key, label]) => {
        const nums = data.balls?.[key] || [];
        const overdueCritical = nums.filter(n => n.overdue_ratio >= 2).length;
        const overdueHigh = nums.filter(n => n.overdue_ratio >= 1.5 && n.overdue_ratio < 2).length;
        const sorted = [...nums].sort((a, b) => {
          let aV = a[sortConfig.key] || 0, bV = b[sortConfig.key] || 0;
          return sortConfig.dir === 'desc' ? bV - aV : aV - bV;
        });
        const topPredicted = new Set([...nums].sort((a, b) => (b.pred_score || 0) - (a.pred_score || 0)).slice(0, 5).map(n => n.number));
        return (
          <TabsContent key={key} value={key}>
            <div className="flex gap-3 mb-4 text-xs font-bold">
              <span className="px-2 py-1 bg-red-100 text-red-800 rounded">CRITICAL (2x+): {overdueCritical}</span>
              <span className="px-2 py-1 bg-orange-100 text-orange-800 rounded">HIGH (1.5x+): {overdueHigh}</span>
            </div>
            <div className="bg-white border border-gray-200 rounded overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-gray-100">
                    <tr>
                      {[['number','#'],['times','Times'],['current_gap','Gap'],['avg_gap','Avg'],['overdue_ratio','Overdue'],['pred_score','Score']].map(([k,l]) => (
                        <th key={k} className="px-2 py-2 text-[10px] uppercase text-gray-700 cursor-pointer hover:bg-gray-200 text-center" onClick={() => handleSort(k)}>{l} <SortIcon k={k} /></th>
                      ))}
                      <th className="px-2 py-2 text-[10px] uppercase text-gray-700 text-center">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sorted.map((n, idx) => (
                      <tr key={n.number} className={topPredicted.has(n.number) ? 'bg-blue-100' : idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="px-2 py-1 border-b font-mono font-bold text-center">{n.number}</td>
                        <td className="px-2 py-1 border-b font-mono text-center">{n.times}</td>
                        <td className={`px-2 py-1 border-b font-mono text-center font-bold ${n.current_gap > n.avg_gap ? 'text-red-600' : 'text-green-700'}`}>{n.current_gap}</td>
                        <td className="px-2 py-1 border-b font-mono text-center">{n.avg_gap}</td>
                        <td className={`px-2 py-1 border-b font-mono text-center font-bold ${n.overdue_ratio > 1.5 ? 'text-red-600' : n.overdue_ratio > 1 ? 'text-orange-600' : 'text-gray-500'}`}>{n.overdue_ratio}x</td>
                        <td className="px-2 py-1 border-b font-mono text-center font-bold">{n.pred_score}</td>
                        <td className="px-2 py-1 border-b text-center"><OverdueBadge ratio={n.overdue_ratio} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="bg-gradient-to-r from-indigo-700 to-blue-800 rounded-lg p-5 mt-4 text-white">
              <div className="flex items-center gap-2 mb-2"><Target size={24} weight="bold" /><h3 className="font-bold">Predicted Next {label}</h3></div>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                {[...nums].sort((a, b) => b.pred_score - a.pred_score).slice(0, 10).map((n, idx) => (
                  <div key={n.number} className={`p-3 rounded-lg ${idx === 0 ? 'bg-sky-100 text-sky-900' : 'bg-white/10'}`}>
                    <div className="text-2xl font-mono font-bold text-center">{n.number}</div>
                    <div className="text-xs text-center mt-1 opacity-80">Gap: {n.current_gap} | {n.overdue_ratio}x</div>
                    <div className="text-center font-bold mt-1">{n.pred_score}</div>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>
        );
      })}
    </Tabs>
  );
}
